import { TokenResponse } from '@react-oauth/google';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { LocalStorageService } from '../../service/LocalStorageService';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { getSpreadsheets, isGoogleSheetsAccessTokenValid } from './helpers';
import { getAPIErrorMessage } from '../../helpers/helpers';
import {
  GoogleSpreadsheet,
  GoogleSpreadsheetWorksheet,
} from 'google-spreadsheet';
import { AuthService } from '../../service/AuthService';
import { useAuthStore } from '../../store/storeAuth';
import { el } from 'date-fns/locale';
import axios from 'axios';

enum GoogleService {
  GoogleSheets = 'Google Sheets',
  GoogleDocs = 'Google Docs',
}

type LoginSuccessTokenResponse = Omit<
  TokenResponse,
  'error' | 'error_description' | 'error_uri'
>;
type LoginErrorResponse = Pick<
  TokenResponse,
  'error' | 'error_description' | 'error_uri'
>;

interface SpreadSheet {
  id: string;
  name: string;
}

const GoogleSheetContext = createContext<{
  checkingConnection: boolean;
  isConnected: boolean;
  googleSheetsAccessToken: undefined | string;
  spreadSheets: SpreadSheet[];
  selectedSpreadSheet: undefined | SpreadSheet;
  workSheets: GoogleSpreadsheetWorksheet[];
  loading: boolean;
  selectedWorkSheet: undefined | GoogleSpreadsheetWorksheet;
  isRefreshing: boolean;
  publicRangeFilter: string;
  selectedPublicGoogleService: GoogleService;
  currentWorkSheetContent: string;
  setSelectedPublicGoogleService: React.Dispatch<
    React.SetStateAction<GoogleService>
  >;
  setIsRefreshing: (isRefreshing: boolean) => void;
  setIsConnected: (isConnected: boolean) => void;
  loginSuccessHandler: (tokenResponse: LoginSuccessTokenResponse) => void;
  loginErrorHandler: (errorResponse: LoginErrorResponse) => void;
  getSpreadSheetsHandler: () => Promise<void>;
  updateSelectedSpreadSheet: (selectedSpreadSheetId: string) => void;
  updateSelectedWorkSheet: (selectedWorkSheetId: number) => void;
  getWorkSheetData: (rangeFilter: string) => Promise<string>;
  refreshSpreadSheets: () => Promise<void>;
  pushDataHandler: (text: string) => Promise<void>;
  disconnectHandler: () => void;
  pushDataToNewSheetHandler: (sheetName: string, text: string) => Promise<void>;
  updatePublicWorkSheets: (publicSheetId: string) => Promise<void>;
  setPublicRangeFilter: React.Dispatch<React.SetStateAction<string>>;
  workSheetsLoaded: boolean;
}>({
  checkingConnection: true,
  isRefreshing: false,
  isConnected: false,
  googleSheetsAccessToken: undefined,
  spreadSheets: [],
  selectedSpreadSheet: undefined,
  workSheets: [],
  selectedWorkSheet: undefined,
  loading: false,
  selectedPublicGoogleService: GoogleService.GoogleSheets,
  publicRangeFilter: '',
  currentWorkSheetContent: '',
  setIsRefreshing: isRefreshing => {},
  setIsConnected: isConnected => {},
  loginSuccessHandler: tokenResponse => {},
  loginErrorHandler(errorResponse) {},
  async getSpreadSheetsHandler() {},
  updateSelectedSpreadSheet(selectedSpreadSheetId) {},
  updateSelectedWorkSheet(selectedWorkSheetId) {},
  getWorkSheetData: async rangeFilter => '',
  async refreshSpreadSheets() {},
  async pushDataHandler(text) {},
  disconnectHandler() {},
  async pushDataToNewSheetHandler(sheetName, text) {},
  async updatePublicWorkSheets(publicSheetId) {},
  setSelectedPublicGoogleService() {},
  setPublicRangeFilter() {},
  workSheetsLoaded: false,
});

const GoogleSheetProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const [isLoading, setIsLoading] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [googleSheetsAccessToken, setGoogleSheetsAccessToken] = useState<
    undefined | string
  >();
  const [spreadSheets, setSpreadSheets] = useState<SpreadSheet[]>([]);
  const [selectedSpreadSheet, setSelectedSpreadSheet] = useState<
    undefined | SpreadSheet
  >();
  const [workSheets, setWorkSheets] = useState<GoogleSpreadsheetWorksheet[]>(
    [],
  );
  const [selectedWorkSheet, setSelectedWorkSheet] = useState<
    undefined | GoogleSpreadsheetWorksheet
  >();
  const [selectedPublicGoogleService, setSelectedPublicGoogleService] =
    useState<GoogleService>(GoogleService.GoogleSheets);
  const [publicRangeFilter, setPublicRangeFilter] = useState('');
  const [currentWorkSheetContent, setCurrentWorkSheetContent] = useState('');
  const [workSheetsLoaded, setWorkSheetsLoaded] = useState(false);

  const { setGoogleLogin, refreshToken, setGoogleToken, googleAccessToken } =
    useAuthStore();

  const disconnectHandlerGoogle = async () => {
    if (!refreshToken) {
      setNotification({
        type: NotificationType.Error,
        message: 'Session expired. Please login again.',
      });
      return;
    }

    await refreshAuthToken(refreshToken);
  };

  useEffect(() => {
    if (googleAccessToken) {
      setGoogleSheetsAccessToken(googleAccessToken);
      setIsConnected(true);
    } else {
      setGoogleSheetsAccessToken(undefined);
      setIsConnected(false);
    }
  }, [googleAccessToken]);

  useEffect(() => {
    const validateExistingGoogleSheetsAccessToken = async () => {
      const googleSheetsAccessToken = LocalStorageService.getItem(
        'googleSheetsAccessToken',
      );

      const refreshToken = LocalStorageService.getItem('googleRefreshToken');

      if (
        googleSheetsAccessToken &&
        (await isGoogleSheetsAccessTokenValid(googleSheetsAccessToken))
      ) {
        setGoogleSheetsAccessToken(googleSheetsAccessToken);
        setIsConnected(true);
      } else if (refreshToken) {
        await refreshAuthToken(refreshToken);
      }
      setCheckingConnection(false);
    };

    validateExistingGoogleSheetsAccessToken();
  }, []);

  const refreshAuthToken = async (refreshToken: string) => {
    try {
      const newTokens = await AuthService.googleRefreshToken({
        refresh_token: refreshToken,
      });
      LocalStorageService.setItem(
        'googleSheetsAccessToken',
        newTokens.data.access_token,
      );
      LocalStorageService.setItem(
        'googleRefreshToken',
        newTokens.data.refresh_token,
      );

      setGoogleSheetsAccessToken(newTokens.data.access_token);
      setIsConnected(true);

      const docs = await getSpreadsheets(newTokens.data.access_token);
      setSpreadSheets(docs);
    } catch (refreshError) {
      // setNotification({
      //   type: NotificationType.Error,
      //   message: 'Failed to refresh session, please login again.',
      // });
      // localStorage.removeItem('googleSheetsAccessToken');
      // localStorage.removeItem('googleRefreshToken');
      // setGoogleToken(null);
    }
  };

  const getSpreadSheetsHandler = useCallback(async () => {
    if (!googleSheetsAccessToken) {
      return disconnectHandlerGoogle();
    }

    try {
      const spreadSheets = await getSpreadsheets(googleSheetsAccessToken);
      setSpreadSheets(spreadSheets);
    } catch (error) {
      let anyError = error as any;
      if (anyError.response.status === 401) {
        disconnectHandlerGoogle();
      } else {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
    }
  }, [googleSheetsAccessToken]);

  const loadSpreadSheetsInfo = useCallback(async () => {
    try {
      await Promise.all([getSpreadSheetsHandler()]);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    }
  }, []);

  const loadWorkSheets = useCallback(
    async (spreadSheetId: string) => {
      setIsLoading(true);
      try {
        const doc = new GoogleSpreadsheet(spreadSheetId, {
          token: googleSheetsAccessToken!,
        });

        await doc.loadInfo();

        const workSheets: GoogleSpreadsheetWorksheet[] = doc.sheetsByIndex;
        setWorkSheets(workSheets);
        setWorkSheetsLoaded(true);
      } catch (error) {
        let anyError = error as any;
        if (anyError.code === 401) {
          disconnectHandlerGoogle();
        } else {
          setNotification({
            type: NotificationType.Error,
            message: getAPIErrorMessage(error),
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [googleSheetsAccessToken],
  );

  // useEffect(() => {
  //   const googleSheetsAccessToken = LocalStorageService.getItem(
  //     'googleSheetsAccessToken'
  //   );
  //   if (googleSheetsAccessToken) {
  //     setIsConnected(true);
  //   } else {
  //     setIsConnected(false);
  //   }
  // }, [googleSheetsAccessToken]);

  const updateSelectedWorkSheet = useCallback(
    async (selectedWorkSheetId: number) => {
      if (selectedWorkSheetId === -1) {
        setSelectedWorkSheet(undefined);
        setWorkSheets([]);

        return;
      }

      setIsLoading(true);
      try {
        const selectedWorkSheet = workSheets.find(
          ({ sheetId }) => sheetId === selectedWorkSheetId,
        );

        if (selectedWorkSheet) {
          setSelectedWorkSheet(selectedWorkSheet);

          await selectedWorkSheet.loadCells();
        } else {
          setSelectedWorkSheet(undefined);
        }
      } catch (error) {
        let anyError = error as any;
        if (anyError.code === 401) {
          disconnectHandlerGoogle();
        } else {
          setNotification({
            type: NotificationType.Error,
            message: getAPIErrorMessage(error),
          });
        }
      } finally {
        setIsLoading(false);
      }
    },

    [workSheets],
  );

  const updateSelectedSpreadSheet = useCallback(
    (selectedSpreadSheetId: string) => {
      const selectedSpreadSheet = spreadSheets.find(
        ({ id }) => id === selectedSpreadSheetId,
      );

      if (selectedSpreadSheet) {
        setSelectedSpreadSheet(selectedSpreadSheet);
        loadWorkSheets(selectedSpreadSheetId);

        setSelectedWorkSheet(undefined);
      } else {
        setSelectedSpreadSheet(undefined);
        setSelectedWorkSheet(undefined);
      }
    },
    [spreadSheets],
  );

  const updatePublicWorkSheets = async (publicSheetId: string) => {
    try {
      setWorkSheets([]);
      const doc = new GoogleSpreadsheet(publicSheetId, {
        apiKey: 'AIzaSyA5ruxonya48UJ6XuhmNcC7x82gmvESeLU',
      });

      await doc.loadInfo();

      const workSheets: GoogleSpreadsheetWorksheet[] = doc.sheetsByIndex;

      setWorkSheets(workSheets);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as any),
      });
    }
  };

  const refreshSpreadSheets = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await getSpreadSheetsHandler();
      if (selectedSpreadSheet) {
        await loadWorkSheets(selectedSpreadSheet.id);
      }
    } catch (error) {
      let anyError = error as any;
      if (anyError.response.status === 401) {
        disconnectHandlerGoogle();
      } else {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
    }
    setIsRefreshing(false);
  }, [selectedSpreadSheet]);

  const getWorkSheetData = useCallback(
    async (rangeFilter: string): Promise<string> => {
      if (!selectedWorkSheet) return 'You have not selected a worksheet';

      let startRow = 0;
      let endRow = selectedWorkSheet.rowCount - 1;
      let startCol = 0;
      let endCol = selectedWorkSheet.columnCount - 1;

      if (rangeFilter !== '') {
        let match;

        match = rangeFilter.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
        if (match) {
          const [, startColStr, startRowStr, endColStr, endRowStr] = match;
          startCol = startColStr.charCodeAt(0) - 65;
          startRow = parseInt(startRowStr, 10) - 1;
          endCol = endColStr.charCodeAt(0) - 65;
          endRow = parseInt(endRowStr, 10) - 1;
        } else {
          match = rangeFilter.match(/^([A-Z]+):([A-Z]+)$/);
          if (match) {
            const [, startColStr, endColStr] = match;
            startCol = startColStr.charCodeAt(0) - 65;
            endCol = endColStr.charCodeAt(0) - 65;
          } else {
            match = rangeFilter.match(/^(\d+):(\d+)$/);
            if (match) {
              const [, startRowStr, endRowStr] = match;
              startRow = parseInt(startRowStr, 10) - 1;
              endRow = parseInt(endRowStr, 10) - 1;
            } else {
              return 'Invalid range format. Please provide the range in the format B1:E5, A:A, or 1:1';
            }
          }
        }
      }

      await selectedWorkSheet.loadCells();

      let textData: string = '';

      for (let row = startRow; row <= endRow; row++) {
        let rowData: string[] = [];

        let numberOfColumnsWithValuePerRow = 0;
        for (let col = startCol; col <= endCol; col++) {
          const cell = selectedWorkSheet.getCell(row, col);

          const cellValue = cell.value === null ? '' : cell.value;

          if (cellValue !== '') {
            numberOfColumnsWithValuePerRow++;
          }
          rowData.push(cellValue as string);
        }

        if (numberOfColumnsWithValuePerRow !== 0) {
          textData += rowData.join(',') + '\n';
        }
      }

      return textData;
    },
    [selectedWorkSheet],
  );

  useEffect(() => {
    const updateCurrentWorkSheetContent = async () => {
      try {
        const currentWorkSheetContent = await getWorkSheetData('');
        setCurrentWorkSheetContent(currentWorkSheetContent);
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      }
    };
    if (selectedWorkSheet) {
      updateCurrentWorkSheetContent();
    }
  }, [selectedWorkSheet, getWorkSheetData]);

  const hasWorkSheetValuesInFirstRow = useCallback(
    async (sheet: GoogleSpreadsheetWorksheet) => {
      await sheet.loadCells();

      const cells = await sheet.getCellsInRange('A1:Z1');

      if (!cells) return false;

      for (let cell of cells) {
        if (cell || cell.value) return true;
      }

      return false;
    },
    [],
  );

  const pushData = useCallback(
    async (sheet: GoogleSpreadsheetWorksheet, text: string) => {
      const textArray = text
        .split('\n')
        .map(item => item.trim())
        .map(item => item.split(','));

      try {
        const hasValuesInFirstRow = await hasWorkSheetValuesInFirstRow(sheet);

        if (hasValuesInFirstRow) {
          await sheet.addRows(textArray);
        } else {
          await sheet.setHeaderRow([...textArray[0]]);
          await sheet.addRows([...textArray.slice(1)]);
        }
      } catch (error) {
        let anyError = error as any;
        if (anyError.code === 401) {
          disconnectHandlerGoogle();
        } else {
          throw error;
        }
      }
    },
    [hasWorkSheetValuesInFirstRow],
  );

  const pushDataToSelectedWorkSheet = useCallback(
    async (text: string) => {
      if (!selectedSpreadSheet?.id || !selectedWorkSheet)
        return setNotification({
          type: NotificationType.Error,
          message: 'You need to select spreadsheet and worksheet',
        });

      try {
        const doc = new GoogleSpreadsheet(selectedSpreadSheet!.id, {
          token: googleSheetsAccessToken!,
        });
        await doc.loadInfo();

        await pushData(selectedWorkSheet, text);
      } catch (error) {
        let anyError = error as any;
        if (anyError.code === 401) {
          disconnectHandlerGoogle();
        } else {
          throw error;
        }
      }
    },
    [selectedSpreadSheet, selectedWorkSheet, googleSheetsAccessToken, pushData],
  );

  const pushDataToNewSheetHandler = useCallback(
    async (sheetName: string, text: string) => {
      try {
        const doc = new GoogleSpreadsheet(selectedSpreadSheet!.id, {
          token: googleSheetsAccessToken!,
        });
        await doc.loadInfo();

        const newSheet = await doc.addSheet({ title: sheetName });

        await pushData(newSheet, text);
      } catch (error) {
        let anyError = error as any;
        if (anyError.code === 401) {
          disconnectHandlerGoogle();
        } else {
          throw error;
        }
      }
    },
    [selectedSpreadSheet, googleSheetsAccessToken, pushData],
  );

  const pushDataHandler = useCallback(
    (text: string) => {
      return pushDataToSelectedWorkSheet(text);
    },
    [pushDataToSelectedWorkSheet],
  );

  useEffect(() => {
    if (isConnected) {
      loadSpreadSheetsInfo();
    }
  }, [isConnected, loadSpreadSheetsInfo]);

  const disconnectHandler = useCallback(() => {
    LocalStorageService.removeItem('googleSheetsAccessToken');
    LocalStorageService.removeItem('googleRefreshToken');
    setIsConnected(false);
    setGoogleSheetsAccessToken(undefined);
    setGoogleLogin(null, null);
    setSpreadSheets([]);
    setSelectedSpreadSheet(undefined);
    setWorkSheets([]);
    setSelectedWorkSheet(undefined);
  }, []);

  const loginSuccessHandler = useCallback(
    (tokenResponse: LoginSuccessTokenResponse) => {
      const googleSheetsAccessToken = tokenResponse.access_token;

      setIsConnected(true);
      setGoogleSheetsAccessToken(googleSheetsAccessToken);
      setSelectedSpreadSheet(undefined);
      setWorkSheets([]);
      setSelectedWorkSheet(undefined);

      LocalStorageService.setItem(
        'googleSheetsAccessToken',
        googleSheetsAccessToken,
      );
    },
    [],
  );

  const loginErrorHandler = useCallback(
    (errorResponse: LoginErrorResponse) => {
      let errorMessage = 'Something went wrong!';
      if (errorResponse.error_description) {
        errorMessage = errorResponse.error_description;
      }

      setNotification({
        type: NotificationType.Error,
        message: errorMessage,
      });
    },
    [setNotification],
  );

  return (
    <GoogleSheetContext.Provider
      value={{
        checkingConnection,
        isConnected,
        googleSheetsAccessToken,
        spreadSheets,
        selectedSpreadSheet,
        workSheets,

        selectedWorkSheet,
        loading: isLoading,
        isRefreshing,
        publicRangeFilter,
        currentWorkSheetContent,
        selectedPublicGoogleService,
        setSelectedPublicGoogleService,
        setIsRefreshing,
        setIsConnected,
        loginSuccessHandler,
        loginErrorHandler,
        getSpreadSheetsHandler,
        updateSelectedSpreadSheet,
        updateSelectedWorkSheet,
        getWorkSheetData,
        refreshSpreadSheets,
        pushDataHandler,
        disconnectHandler,
        pushDataToNewSheetHandler,
        updatePublicWorkSheets,
        setPublicRangeFilter,
        workSheetsLoaded,
      }}
    >
      {children}
    </GoogleSheetContext.Provider>
  );
};

const useGoogleSheet = function () {
  const context = useContext(GoogleSheetContext);

  if (!context)
    throw new Error(
      'GoogleSheetContext is used outside of GoogleSheetProvider',
    );

  return context;
};

export { useGoogleSheet, GoogleSheetProvider };
